<template>
    <div class="practice-container" :class="{ 'exam-mode': examMode }">
        <!-- Guest Banner (shown to unauthenticated users) -->
        <GuestBanner
            v-if="userType === 'guest'"
            message="Guest - your progress won't be saved"
        />

        <!-- Main Content -->
        <div class="main-content">
            <!-- Loading State -->
            <LoadingSpinner v-if="loading" />

            <!-- Question Display -->
            <div v-else-if="currentQuestion" class="question-section">
                <!-- Question Header -->
                <!-- Question Header with integrated filter -->
                <QuestionHeader
                    :difficulty="currentQuestion.difficulty"
                    :questionType="currentQuestion.question_type"
                    :maxMarks="currentQuestion.max_marks"
                    :topics="currentQuestion.topic"
                    :formattedTime="formattedStopwatch"
                    :remainingQuestions="remainingQuestions"
                    :userType="userType"
                >
                    <template #filter>
                        <QuestionFilter
                            :currentQuestion="currentQuestion"
                            :activeFilters="activeFilters"
                            :disabled="!canUseFilter"
                            @filter-applied="handleFilterApplied"
                            compact
                        />
                    </template>
                </QuestionHeader>

                <!-- Question Text -->
                <QuestionDisplay
                    :sharedContext="currentQuestion.shared_context"
                    :questionText="currentQuestion.question_text"
                    :imageUrls="currentQuestion.image_urls"
                    :questionId="currentQuestion.id"
                />

                <!-- Multiple Choice Options -->
                <MultipleChoiceOptions
                    v-if="currentQuestion.question_type === 'multiple_choice'"
                    v-model="userAnswer"
                    :options="multipleChoiceOptions"
                    :disabled="submitted"
                />

                <!-- Text Input for Other Question Types -->
                <AnswerInput
                    v-else
                    v-model="userAnswer"
                    :disabled="submitted"
                    :canvasVisible="canvasVisible && !submitted && canUseCanvas"
                    :canUseCanvas="canUseCanvas"
                    @toggle-canvas="toggleCanvas"
                    @math-enter="handleMathEnter"
                >
                    <template #canvas>
                        <DrawingCanvas
                            v-if="canUseCanvas"
                            :visible="canvasVisible"
                            v-model:savedData="savedCanvasData"
                            @save="saveCanvasToThumbnail"
                            ref="drawingCanvasRef"
                        />
                    </template>
                </AnswerInput>

                <!-- Conversion History Preview -->
                <ConversionHistory
                    v-if="conversionHistory.length"
                    v-model="conversionHistory"
                    @delete="deleteThumbnail"
                />

                <!-- Hints Section -->
                <HintsSection
                    v-if="!submitted && canUseHints"
                    :hints="[
                        currentQuestion.hint_1,
                        currentQuestion.hint_2,
                        currentQuestion.hint_3,
                    ]"
                    :hintsRevealed="hintsRevealed"
                    :hintPenalty="HINT_PENALTY"
                    @reveal="revealHint"
                />

                <!-- Feedback Section (After Submission) -->
                <FeedbackCard
                    v-if="submitted && feedback"
                    :feedback="feedback"
                />

                <FeedbackPrompt
                    compact
                    class="inline-floating"
                    promptText="How was this question?"
                    :contextData="{
                        page: 'practice',
                        questionId: currentQuestion?.id,
                        difficulty: currentQuestion?.difficulty,
                    }"
                />
            </div>

            <!-- No Questions State -->
            <div v-else class="no-questions">
                <h3>No questions available</h3>
                <p>Please review your filters or check back later</p>
                <div class="filter-wrapper">
                    <QuestionFilter
                        :currentQuestion="currentQuestion"
                        :activeFilters="activeFilters"
                        @filter-applied="handleFilterApplied"
                        compact
                    />
                </div>
            </div>
        </div>

        <!-- Sticky Action Button -->
        <div v-if="currentQuestion && !loading" class="sticky-action-bar">
            <button
                v-if="!submitted"
                @click="submitAnswer"
                class="submit-btn"
                :disabled="!userAnswer || submitting"
            >
                {{ submitting ? "Submitting..." : "Submit Answer" }}
            </button>
            <button v-else @click="loadNextQuestion" class="next-btn">
                Next Question
            </button>
        </div>

        <!-- Verification Modal -->
        <VerificationModal
            v-if="showVerifyModal && selectedItem"
            :item="selectedItem"
            :molecule="molecule"
            @keep="keepAndSubmit"
            @redraw="redrawMolecule"
            @cancel="cancelVerification"
        />

        <!-- Limit Reached Modal -->
        <div
            v-if="showLimitModal"
            class="limit-modal-overlay"
            @click.self="showLimitModal = false"
        >
            <div class="limit-modal">
                <h2>Daily Limit Reached</h2>
                <p v-if="userType === 'guest'">
                    You've used all 3 free questions for today. Create a free
                    account to get 5 questions per day!
                </p>
                <p v-else-if="userType === 'free'">
                    You've used all 5 free questions for today. Upgrade to
                    Student plan for unlimited questions!
                </p>
                <p class="reset-info">Resets at midnight AEST</p>
                <div class="limit-modal-actions">
                    <button
                        v-if="userType === 'guest'"
                        @click="$router.push('/register')"
                        class="primary-btn"
                    >
                        Create Free Account
                    </button>
                    <button
                        v-else-if="userType === 'free'"
                        @click="$router.push('/profile')"
                        class="primary-btn"
                    >
                        Upgrade Plan
                    </button>
                    <button
                        @click="showLimitModal = false"
                        class="secondary-btn"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import api from "@/services/api";

// Components
import GuestBanner from "@/components/common/GuestBanner.vue";
import LoadingSpinner from "@/components/practice/LoadingSpinner.vue";
import QuestionHeader from "@/components/practice/QuestionHeader.vue";
import QuestionDisplay from "@/components/practice/QuestionDisplay.vue";
import MultipleChoiceOptions from "@/components/practice/MultipleChoiceOptions.vue";
import AnswerInput from "@/components/practice/AnswerInput.vue";
import DrawingCanvas from "@/components/practice/DrawingCanvas.vue";
import ConversionHistory from "@/components/practice/ConversionHistory.vue";
import HintsSection from "@/components/practice/HintsSection.vue";
import FeedbackCard from "@/components/practice/FeedbackCard.vue";
import VerificationModal from "@/components/practice/VerificationModal.vue";
import QuestionFilter from "@/components/practice/QuestionFilter.vue";
import FeedbackPrompt from "@/components/common/FeedbackPrompt.vue";

// Composables
import { useStopwatch } from "@/composables/useStopwatch";
import { useImageProcessing } from "@/composables/useImageProcessing";
import { useFingerprint } from "@/composables/useFingerprint";
import { useGuestLimit } from "@/composables/useGuestLimit";
import { useFeatureAccess } from "@/composables/useFeatureAccess";

export default {
    name: "Practice",
    components: {
        GuestBanner,
        LoadingSpinner,
        QuestionHeader,
        QuestionDisplay,
        MultipleChoiceOptions,
        AnswerInput,
        DrawingCanvas,
        ConversionHistory,
        HintsSection,
        FeedbackCard,
        VerificationModal,
        QuestionFilter,
        FeedbackPrompt,
    },
    setup() {
        // ==================== SETUP ====================
        const router = useRouter();
        const authStore = useAuthStore();

        // Composables
        const {
            formattedStopwatch,
            startStopwatch,
            stopStopwatch,
            resetStopwatch,
            getTimeInSeconds,
        } = useStopwatch();

        const {
            conversionHistory,
            showVerifyModal,
            selectedItem,
            molecule,
            convertAllThumbnails,
            saveCanvasToThumbnail,
            deleteThumbnail,
            keepAndSubmit,
            redrawMolecule,
            cancelVerification,
        } = useImageProcessing();

        // Fingerprinting and rate limiting
        const { initialize: initFingerprint } = useFingerprint();
        const {
            remainingQuestions,
            hasReachedLimit,
            userType,
            fetchLimitStatus,
            updateFromResponse,
            getFingerprintForSubmission,
        } = useGuestLimit();
        const { canUseCanvas, canUseFilter, canUseHints, fetchFeatureAccess } =
            useFeatureAccess();

        // ==================== CONSTANTS ====================
        const HINT_PENALTY = 0.5;
        const showLimitModal = ref(false);

        // ==================== STATE ====================
        const loading = ref(true);
        const submitting = ref(false);
        const submitted = ref(false);

        const currentQuestion = ref(null);
        const userAnswer = ref("");
        const hintsRevealed = ref(0);
        const feedback = ref(null);

        const canvasVisible = ref(false);
        const savedCanvasData = ref(null);
        const drawingCanvasRef = ref(null);

        // Load filters from localStorage or use defaults
        const loadSavedFilters = () => {
            const saved = localStorage.getItem("questionFilters");
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    return {
                        topics: parsed.topics || [],
                        difficulties: parsed.difficulties || [],
                        types: parsed.types || [],
                        filterMode: parsed.filterMode || 'AND'
                    };
                } catch {
                    return { topics: [], difficulties: [], types: [], filterMode: 'AND' };
                }
            }
            return { topics: [], difficulties: [], types: [], filterMode: 'AND' };
        };

        const activeFilters = ref(loadSavedFilters());

        // ==================== COMPUTED ====================
        const user = computed(() => authStore.user);
        // Exam mode requires Tier 2+ subscription
        const examMode = computed(() => {
            const hasTier = authStore.hasTierAccess("TIER 2");
            const isEnabled =
                authStore.settings?.appearance?.exam_mode ?? false;
            return hasTier && isEnabled;
        });

        const multipleChoiceOptions = computed(() => {
            if (!currentQuestion.value) return [];

            const options = [];
            if (currentQuestion.value.option_a) {
                options.push({
                    value: "A",
                    text: currentQuestion.value.option_a,
                });
            }
            if (currentQuestion.value.option_b) {
                options.push({
                    value: "B",
                    text: currentQuestion.value.option_b,
                });
            }
            if (currentQuestion.value.option_c) {
                options.push({
                    value: "C",
                    text: currentQuestion.value.option_c,
                });
            }
            if (currentQuestion.value.option_d) {
                options.push({
                    value: "D",
                    text: currentQuestion.value.option_d,
                });
            }

            return options;
        });

        // ==================== METHODS ====================

        const loadQuestion = async () => {
            loading.value = true;

            try {
                const response = await api.getQuestions({
                    limit: 1,
                    user_id: user.value?.id,
                });

                if (response.data && response.data.length > 0) {
                    currentQuestion.value = response.data[0];

                    // Reset state for new question
                    userAnswer.value = "";
                    hintsRevealed.value = 0;
                    submitted.value = false;
                    feedback.value = null;
                    conversionHistory.value = [];
                } else {
                    currentQuestion.value = null;
                }
            } catch (error) {
                console.error("Error loading question:", error);
                alert("Failed to load question. Please try again.");
            } finally {
                loading.value = false;
            }
        };

        const revealHint = (hintNumber) => {
            hintsRevealed.value = hintNumber;
        };

        const handleMathEnter = (value) => {
            if (!value) return;

            // Check if it's display mode ($$...$$) or inline mode ($...$)
            const isDisplayMode = value.startsWith('$$');

            if (userAnswer.value) {
                if (isDisplayMode) {
                    // Display mode: add newline before and after
                    userAnswer.value += "\n" + value + "\n";
                } else {
                    // Inline mode: add space before (no newline)
                    userAnswer.value += " " + value;
                }
            } else {
                userAnswer.value = value;
            }
        };

        const toggleCanvas = () => {
            canvasVisible.value = !canvasVisible.value;
        };

        const submitAnswer = async () => {
            stopStopwatch();

            if (!userAnswer.value.trim()) {
                alert("Please enter an answer");
                return;
            }

            // Check if user has reached their daily limit
            if (hasReachedLimit.value) {
                showLimitModal.value = true;
                return;
            }

            const hasUnconverted = conversionHistory.value.some(
                (item) => !item.latex,
            );
            if (hasUnconverted) {
                const result = await convertAllThumbnails();

                // If user clicked "Redraw", abort submission so they can fix their drawing
                if (result?.redrawRequested) {
                    startStopwatch();
                    return;
                }

                // If user clicked "Cancel", abort submission
                if (result?.cancelled) {
                    startStopwatch();
                    return;
                }

                // Auto-insert all converted LaTeX
                const allLatex = conversionHistory.value
                    .filter(
                        (item) =>
                            item.latex && item.latex !== "[Conversion failed]",
                    )
                    .map((item) => item.latex)
                    .join("\n\n");

                if (allLatex) {
                    userAnswer.value += "\n\n" + allLatex;
                }
            }

            try {
                submitting.value = true;

                // Get fingerprint for guest users
                const fingerprint = await getFingerprintForSubmission();

                // Prepare submission data
                const chemicalStructures = conversionHistory.value
                    .filter((item) => item.chemicalData)
                    .map((item, index) => ({
                        index: index + 1,
                        iupacFromSmiles: item.chemicalData.iupacFromSmiles,
                        iupacFromInchi: item.chemicalData.iupacFromInchi,
                        smiles: item.chemicalData.smiles,
                        inchi: item.chemicalData.inchi,
                        inchiKey: item.chemicalData.inchiKey,
                    }));

                const submissionData = {
                    user_id: user.value?.id || null,
                    user_answer: userAnswer.value.trim(),
                    time_taken_seconds: getTimeInSeconds(),
                    hints_used: hintsRevealed.value,
                    chemical_structures:
                        chemicalStructures.length > 0
                            ? chemicalStructures
                            : null,
                    fingerprint: fingerprint,
                };

                const response = await api.submitAnswer(
                    currentQuestion.value.id,
                    submissionData,
                );

                // Update limit tracking from response
                if (response.data.questions_remaining !== undefined) {
                    updateFromResponse(response.data);
                }

                // Store feedback
                feedback.value = response.data;
                submitted.value = true;
            } catch (error) {
                console.error("Error submitting answer:", error);

                // Handle rate limit error specifically
                if (error.response?.status === 429) {
                    showLimitModal.value = true;
                    return;
                }

                // More detailed error logging
                if (error.response) {
                    console.error("Response status:", error.response.status);
                    console.error("Response data:", error.response.data);
                    console.error("Response headers:", error.response.headers);

                    // Show specific error message if available
                    const errorMessage =
                        error.response.data?.detail ||
                        error.response.data?.message ||
                        error.response.data?.error ||
                        "Failed to submit answer. Please try again.";
                    alert(errorMessage);
                } else if (error.request) {
                    console.error("No response received:", error.request);
                    alert(
                        "No response from server. Please check your connection.",
                    );
                } else {
                    console.error("Error message:", error.message);
                    alert(
                        error.message ||
                            "Failed to submit answer. Please try again.",
                    );
                }
            } finally {
                submitting.value = false;
            }
        };

        const loadNextQuestion = () => {
            if (
                activeFilters.value.topics.length > 0 ||
                activeFilters.value.difficulties.length > 0 ||
                activeFilters.value.types.length > 0
            ) {
                loadFilteredQuestion();
            } else {
                loadQuestion();
            }
            resetStopwatch();
            startStopwatch();
        };

        const handleFilterApplied = async (filters) => {
            // Store the active filters
            activeFilters.value = {
                topics: filters.topics,
                difficulties: filters.difficulties,
                types: filters.types,
                filterMode: filters.filterMode || 'AND',
            };

            // Persist to localStorage
            localStorage.setItem(
                "questionFilters",
                JSON.stringify(activeFilters.value),
            );

            // If current question doesn't match, fetch a new one
            if (filters.shouldFetchNew) {
                await loadFilteredQuestion();
            }
        };

        const loadFilteredQuestion = async () => {
            loading.value = true;

            try {
                const response = await api.getQuestions({
                    topics: activeFilters.value.topics,
                    difficulties: activeFilters.value.difficulties,
                    question_types: activeFilters.value.types,
                    filter_mode: activeFilters.value.filterMode || 'AND',
                    limit: 10,
                    user_id: user.value?.id,
                });

                if (response.data && response.data.length > 0) {
                    currentQuestion.value = response.data[0];

                    // Reset state
                    userAnswer.value = "";
                    hintsRevealed.value = 0;
                    submitted.value = false;
                    feedback.value = null;
                    conversionHistory.value = [];

                    resetStopwatch();
                    startStopwatch();
                } else {
                    alert(
                        "No questions match your filters. Try adjusting them.",
                    );
                }
            } catch (error) {
                console.error("Error loading filtered question:", error);
                alert("Failed to load question. Please try again.");
            } finally {
                loading.value = false;
            }
        };

        const handleNavigation = (tabId) => {
            // Handle navigation logic
        };

        // ==================== LIFECYCLE ====================
        onMounted(async () => {
            // Initialize fingerprinting for rate limiting
            await initFingerprint();

            // Fetch user info only if authenticated
            if (authStore.isAuthenticated) {
                await authStore.fetchUser();
            }

            // Fetch limit status and feature access (works for guests too)
            await fetchLimitStatus();
            await fetchFeatureAccess();

            // Load filtered question if filters exist, otherwise load any question
            if (
                activeFilters.value.topics.length > 0 ||
                activeFilters.value.difficulties.length > 0 ||
                activeFilters.value.types.length > 0
            ) {
                loadFilteredQuestion();
            } else {
                loadQuestion();
            }
            startStopwatch();
        });

        // ==================== RETURN ====================
        return {
            HINT_PENALTY,
            loading,
            submitting,
            submitted,
            currentQuestion,
            userAnswer,
            hintsRevealed,
            feedback,
            user,
            examMode,
            multipleChoiceOptions,
            formattedStopwatch,
            canvasVisible,
            savedCanvasData,
            drawingCanvasRef,
            conversionHistory,
            showVerifyModal,
            selectedItem,
            molecule,
            loadQuestion,
            revealHint,
            handleMathEnter,
            toggleCanvas,
            submitAnswer,
            loadNextQuestion,
            saveCanvasToThumbnail,
            deleteThumbnail,
            keepAndSubmit,
            redrawMolecule,
            cancelVerification,
            activeFilters,
            handleFilterApplied,
            loadFilteredQuestion,
            handleNavigation,
            // Rate limiting
            remainingQuestions,
            hasReachedLimit,
            userType,
            showLimitModal,
            // Feature access
            canUseCanvas,
            canUseFilter,
            canUseHints,
        };
    },
};
</script>

<style scoped>
* {
    font-family: "Space Grotesk";
    font-weight: 400;
    letter-spacing: 1px;
}

::-webkit-scrollbar {
    width: 20px;
}

::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 10px;
}

.practice-container {
    min-height: 100vh;
    background: var(--bg-primary);
    justify-content: center;
}

.main-content {
    width: 100%;
    max-width: min(90%, 1200px); /* Better balance */
    margin: 0 auto;
    padding: 40px; /* Increased padding for breathing room */
    padding-bottom: 100px; /* Space for sticky button */
}

@media (max-width: 1400px) {
    .main-content {
        padding: 0px;
        padding-bottom: 100px; /* Space for sticky button */
    }
}

@media (max-width: 1200px) {
    .main-content {
        max-width: 100%;
        padding: 0px;
        padding-bottom: 100px; /* Space for sticky button */
    }
}

@media (max-width: 768px) {
    .main-content {
        padding-bottom: 170px; /* Space for sticky button + mobile navbar */
    }
}

.question-section {
    background: var(--bg-primary);
    padding: 25px;
    text-align: center;
}

/* Inline floating feedback - keeps floating appearance but in document flow */
.question-section :deep(.inline-floating) {
    position: static !important;
    margin-top: 30px;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    bottom: auto !important;
    right: auto !important;
    left: auto !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8), 0 0 30px rgba(var(--accent-rgb), 0.2);
    background-color: var(--bg-secondary, #1a1a1a);
    border: 2px solid rgba(var(--accent-rgb), 0.6);
}

/* Sticky Action Bar */
.sticky-action-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 20px;
    background: var(--bg-primary);
    border-top: 1px solid rgba(var(--accent-rgb), 0.2);
    z-index: 100;
    display: flex;
    justify-content: center;
}

@media (max-width: 768px) {
    .sticky-action-bar {
        bottom: 70px; /* Above mobile navbar */
    }
}

.sticky-action-bar .submit-btn,
.sticky-action-bar .next-btn {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
}

.submit-btn {
    width: 100%;
    padding: 16px;
    background: rgba(var(--accent-rgb), 0.8);
    color: var(--text-primary);
    border: 2px solid rgba(var(--accent-rgb), 1);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.next-btn {
    width: 100%;
    padding: 16px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition:
        transform 0.2s,
        box-shadow 0.2s;
}

.next-btn:hover {
    transform: translateY(-2px);
}

.no-questions {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    background: rgba(19, 19, 19, 0.6);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
}

.no-questions h3 {
    margin-bottom: 10px;
    color: var(--text-secondary);
}

.no-questions p {
    color: var(--text-primary);
    opacity: 0.8;
}

.no-questions .filter-wrapper {
    max-width: 90%;
    margin: 20px auto 0;
    padding: 0 20px;
}

.no-questions .filter-wrapper :deep(.filter-toggle-btn) {
    font-size: 16px;
    padding: 12px 24px;
}

.no-questions .filter-wrapper :deep(.filter-toggle-btn svg) {
    width: 24px;
    height: 24px;
}

/* ==================== EXAM MODE ==================== */
.practice-container.exam-mode {
    background: #ffffff;
}

.practice-container.exam-mode .question-section {
    background: #ffffff;
    border: none;
    box-shadow: none;
}

.practice-container.exam-mode,
.practice-container.exam-mode .question-section,
.practice-container.exam-mode :deep(.question-text),
.practice-container.exam-mode :deep(.question-content),
.practice-container.exam-mode :deep(.question-text p),
.practice-container.exam-mode :deep(.question-text li),
.practice-container.exam-mode :deep(.question-text td),
.practice-container.exam-mode :deep(.question-text th) {
    font-family: "Times New Roman", Times, serif;
    color: #000000 !important;
}

.practice-container.exam-mode :deep(.question-text h3) {
    color: #000000;
}

.practice-container.exam-mode :deep(.question-text table) {
    border-color: #000000;
}

.practice-container.exam-mode :deep(.question-text th),
.practice-container.exam-mode :deep(.question-text td) {
    border-color: #000000;
    background: transparent;
}

.practice-container.exam-mode :deep(.question-text th) {
    background: #f0f0f0;
}

.practice-container.exam-mode .sticky-action-bar {
    background: #ffffff;
    border-top: 1px solid #000000;
}

.practice-container.exam-mode .submit-btn,
.practice-container.exam-mode .next-btn {
    background: #000000;
    color: #ffffff;
    border: none;
}

.practice-container.exam-mode .submit-btn:hover:not(:disabled),
.practice-container.exam-mode .next-btn:hover {
    background: #333333;
}

/* Question Header - minimal */
.practice-container.exam-mode :deep(.question-header) {
    border-bottom: 1px solid #000000;
}

.practice-container.exam-mode :deep(.badge),
.practice-container.exam-mode :deep(.topic-tag) {
    background: transparent;
    border: 1px solid #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.badge-difficulty),
.practice-container.exam-mode :deep(.badge-difficulty[data-difficulty]) {
    background: transparent;
    border-color: #000000;
    color: #000000;
}

.practice-container.exam-mode :deep(.stopwatch-display) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

/* Answer Input - minimal */
.practice-container.exam-mode :deep(.answer-input label) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.answer-input textarea) {
    background: #ffffff;
    border: 1px solid #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.answer-input textarea:focus) {
    border-color: #000000;
    outline: 1px solid #000000;
}

.practice-container.exam-mode :deep(#mathfield) {
    background: #ffffff;
    border: 1px solid #000000;
    color: #000000;
}

.practice-container.exam-mode :deep(#mathfield:focus) {
    border-color: #000000 !important;
}

.practice-container.exam-mode :deep(.mf-insert-btn),
.practice-container.exam-mode :deep(.mf-toggle-canvas-btn) {
    background: #ffffff;
    border: 1px solid #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.mf-insert-btn:hover),
.practice-container.exam-mode :deep(.mf-toggle-canvas-btn:hover),
.practice-container.exam-mode :deep(.mf-toggle-canvas-btn.active) {
    background: #f0f0f0;
    border-color: #000000;
}

/* Question text spacing */
.practice-container :deep(.question-text h3) {
    margin-bottom: 20px;
}

/* Hints Section - hidden in exam mode */
.practice-container.exam-mode :deep(.hints-section) {
    display: none;
}

/* Filter Button - minimal */
.practice-container.exam-mode :deep(.filter-toggle-btn) {
    background: #ffffff;
    border: 1px solid #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.filter-toggle-btn:hover) {
    background: #f0f0f0;
    border-color: #000000;
}

.practice-container.exam-mode :deep(.filter-badge) {
    background: #000000;
    color: #ffffff;
}

/* Multiple Choice Options - minimal */
.practice-container.exam-mode :deep(.options) {
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.option-label) {
    background: #ffffff;
    border: 1px solid #000000;
}

.practice-container.exam-mode :deep(.option-label:hover) {
    background: #f5f5f5;
    border-color: #000000;
}

.practice-container.exam-mode :deep(.option-label.selected) {
    background: #e8e8e8;
    border-color: #000000;
}

.practice-container.exam-mode :deep(.option-text) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

/* KaTeX - serif in exam mode */
.practice-container.exam-mode :deep(.katex),
.practice-container.exam-mode :deep(.katex-html),
.practice-container.exam-mode :deep(.katex .base),
.practice-container.exam-mode :deep(.katex .strut),
.practice-container.exam-mode :deep(.katex .textord),
.practice-container.exam-mode :deep(.katex .mord),
.practice-container.exam-mode :deep(.katex .mord.text),
.practice-container.exam-mode :deep(.katex .text),
.practice-container.exam-mode :deep(.katex .mathrm),
.practice-container.exam-mode :deep(.katex .minner),
.practice-container.exam-mode :deep(.katex span) {
    font-family: "Times New Roman", Times, serif !important;
    color: #000000;
}

/* Feedback Card - exam mode */
.practice-container.exam-mode :deep(.feedback-section) {
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.feedback-card) {
    background: #ffffff;
}

.practice-container.exam-mode :deep(.feedback-card.correct) {
    background: #ffffff;
    border-color: #4caf50;
}

.practice-container.exam-mode :deep(.feedback-card.incorrect) {
    background: #ffffff;
    border-color: #f44336;
}

.practice-container.exam-mode :deep(.feedback-header h3) {
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode
    :deep(.feedback-card.correct .feedback-header h3) {
    color: #4caf50;
}

.practice-container.exam-mode
    :deep(.feedback-card.incorrect .feedback-header h3) {
    color: #f44336;
}

.practice-container.exam-mode :deep(.feedback-header .score) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.feedback-text strong),
.practice-container.exam-mode :deep(.worked-solution strong) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.feedback-text p),
.practice-container.exam-mode :deep(.worked-solution p) {
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.worked-solution) {
    background: #f5f5f5;
}

.practice-container.exam-mode :deep(.worked-solution th),
.practice-container.exam-mode :deep(.worked-solution td) {
    border-color: #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.practice-container.exam-mode :deep(.worked-solution th) {
    background: #e8e8e8;
}

.practice-container.exam-mode :deep(.worked-solution tr:nth-child(even)) {
    background: #f0f0f0;
}

/* ==================== LIMIT MODAL ==================== */
.limit-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.limit-modal {
    background: var(--bg-secondary, #1a1a1a);
    border: 1px solid rgba(var(--accent-rgb), 0.4);
    padding: 40px;
    max-width: 450px;
    text-align: center;
}

.limit-modal h2 {
    color: var(--accent);
    margin-bottom: 16px;
    font-size: 24px;
}

.limit-modal p {
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.6;
}

.limit-modal .reset-info {
    color: var(--text-secondary);
    font-size: 14px;
    margin-bottom: 24px;
}

.limit-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.limit-modal .primary-btn {
    padding: 12px 24px;
    background: var(--accent);
    color: var(--bg-primary);
    border: none;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s;
}

.limit-modal .primary-btn:hover {
    transform: translateY(-2px);
}

.limit-modal .secondary-btn {
    padding: 12px 24px;
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--text-secondary);
    cursor: pointer;
    transition: border-color 0.2s;
}

.limit-modal .secondary-btn:hover {
    border-color: var(--text-primary);
}
</style>
